/**
 * EvaluationFormV2 — Phase 3A
 * --------------------------------------------------------------
 * Trainova Academy Evaluations V2 — mobile-first, one-question-at-a-time
 * flow that renders development / status / reflection sections distinctly.
 *
 * Source of truth (read these before changing anything):
 *   - TEMPLATES_V2_REVIEW_DRAFT.md   (section_type semantics)
 *   - TRAINOVA_RATING_PHILOSOPHY.md  (status must feel softer, never as weakness)
 *   - QUESTION_DESIGN_PRINCIPLES.md  (academy language)
 *
 * Props
 *   template       — V2 template object from /api/templates/v2/{id}
 *   evaluationId   — optional, present for submission flow
 *   onSubmit       — async (responses) => void  (parent handles backend call)
 *   onCancel       — () => void
 *   playerName     — optional, shown in header for coach assessments
 *
 * The component is intentionally self-contained: no global state, no API calls.
 * Parent supplies the template (already fetched) and handles submit.
 */
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Sparkles,
  Heart,
  PenLine,
  Megaphone,
  X,
} from "lucide-react";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Progress } from "../ui/progress";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";

import RatingScale from "./RatingScale";
import EmojiScale from "./EmojiScale";
import PainLocationPicker from "./PainLocationPicker";

// Pain is distinct from fatigue/load; mirror backend evaluation validation.
const PAIN_FOLLOWUP_THRESHOLD = 7;

function isPainQuestion(question) {
  if (!question) return false;
  const metric = String(question.metric || "").toLowerCase();
  if (metric) {
    return metric === "pain" || metric === "injury_risk" || question.is_pain_question === true;
  }
  if (question.is_pain_question === true) return true;
  // Legacy/custom questions without a metric: avoid matching "ont" in "kontroll".
  const text = `${question.text || ""} ${String(question.id || "").replace(/_/g, " ")}`;
  return /\b(pain|injury|smärta|ont|skada)\b/i.test(text);
}

// Visual semantics per section_type. Status uses cool/calm palette so it
// never reads as a "weakness". Development is warm/active. Reflection is neutral.
// Meta is the "Ledarfeedback" section where the player rates the coaching staff
// — visually isolated so it never reads as a strength of the player.
//
// `intro` copy is rendered as a tiny subtitle below the section pill on the
// FIRST question of each section — never as a standalone screen. This
// preserves rhythm and avoids dead "Fortsätt" pauses.
//
// Localized via `useSectionStyle(t)` — labels/intro come from i18n resource
// bundles so English users see English copy while shared visual tokens stay
// language-neutral.
const SECTION_ICONS = {
  development: Sparkles,
  status: Heart,
  reflection: PenLine,
  meta: Megaphone,
};

const SECTION_CHIP_CLASSES = {
  development: "bg-emerald-500/10 text-emerald-700 border-emerald-200",
  status: "bg-sky-500/10 text-sky-700 border-sky-200",
  reflection: "bg-[rgb(var(--tn-bg-71717a))]/10 text-zinc-700 border-[rgb(var(--tn-border-e4e4e7))]",
  meta: "bg-amber-500/10 text-amber-700 border-amber-200",
};

const SECTION_ACCENT = {
  development: "from-emerald-500/90 to-emerald-600/90",
  status: "from-sky-500/90 to-indigo-500/90",
  reflection: "from-[rgb(var(--tn-bg-71717a))]/90 to-[rgb(var(--tn-bg-52525b))]/90",
  meta: "from-amber-500/90 to-orange-500/90",
};

function buildSectionStyle(t) {
  const build = (key) => ({
    label: t(`development:eval_flow.section_labels.${key}`),
    icon: SECTION_ICONS[key],
    accent: SECTION_ACCENT[key],
    chip: SECTION_CHIP_CLASSES[key],
    intro: t(`development:eval_flow.section_intros.${key}`),
  });
  return {
    development: build("development"),
    status: build("status"),
    reflection: build("reflection"),
    meta: build("meta"),
  };
}

const SECTION_ORDER = ["development", "status", "reflection", "meta"];

function buildSteps(template) {
  // Steps = [question, question, ..., question, summary]
  // NO standalone section-intro steps — section transitions happen
  // *inside* the question card via the section pill + subtitle.
  const qs = (template?.questions || []).slice().sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );
  const bySection = { development: [], status: [], reflection: [], meta: [] };
  qs.forEach((q) => {
    const s = q.section_type || "development";
    if (bySection[s]) bySection[s].push(q);
  });

  const steps = [];
  SECTION_ORDER.forEach((sec) => {
    bySection[sec].forEach((q, idx) => {
      steps.push({
        kind: "question",
        section: sec,
        question: q,
        // Mark the first question of each section so the card can render
        // the intro subtitle once.
        isSectionStart: idx === 0,
      });
    });
  });
  steps.push({ kind: "summary" });
  return steps;
}

const cardVariants = {
  enter: (dir) => ({ x: dir > 0 ? 40 : -40, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -40 : 40, opacity: 0 }),
};

export default function EvaluationFormV2({
  template,
  evaluationId,
  onSubmit,
  onCancel,
  playerName,
}) {
  const { t } = useTranslation(['development']);
  const SECTION_STYLE = useMemo(() => buildSectionStyle(t), [t]);
  const steps = useMemo(() => buildSteps(template), [template]);
  const [stepIndex, setStepIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const step = steps[stepIndex];
  const totalQuestions = steps.filter((s) => s.kind === "question").length;
  const answeredCount = Object.values(answers).filter(
    (v) => v !== undefined && v !== ""
  ).length;
  const progressPct =
    totalQuestions === 0
      ? 0
      : Math.min(100, Math.round((answeredCount / totalQuestions) * 100));

  const isCoach = !!template?.is_coach_assessment;
  const estSeconds = template?.v2_metadata?.estimated_completion_seconds || 60;

  const goNext = () => {
    if (stepIndex < steps.length - 1) {
      setDirection(1);
      setStepIndex(stepIndex + 1);
    }
  };
  const goBack = () => {
    if (stepIndex > 0) {
      setDirection(-1);
      setStepIndex(stepIndex - 1);
    }
  };

  const setAnswer = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
  };

  const canAdvance = (() => {
    if (step?.kind !== "question") return true;
    const q = step.question;
    if (!q.required) return true;
    const v = answers[q.id];
    if (q.type === "text") return true; // text is always optional in v2
    return v !== undefined && v !== null && v !== "";
  })();

  const handleSubmit = async () => {
    const responses = (template.questions || []).map((q) => {
      const a = answers[q.id];
      return {
        question_id: q.id,
        rating: typeof a === "number" ? a : null,
        answer: a ?? "",
        metric: q.metric || null,
        core_metric: q.core_metric || null,
        section_type: q.section_type || null,
      };
    });
    // Append pain follow-up answers (one set per triggering pain question).
    // Structured payload: pain_area, pain_side, pain_duration — feeds
    // pain_events / lagstatus_v2 / future prevention insights.
    Object.keys(answers).forEach((key) => {
      if (key.endsWith("__pain_loc")) {
        const baseQid = key.replace("__pain_loc", "");
        const loc = answers[key] || {};
        if (loc?.area) {
          responses.push({
            question_id: `${baseQid}__pain_area`,
            answer: loc.area,
            metric: "pain_location",
            core_metric: "load",
            section_type: "status",
          });
          if (loc.side) {
            responses.push({
              question_id: `${baseQid}__pain_side`,
              answer: loc.side,
              metric: "pain_side",
              core_metric: "load",
              section_type: "status",
            });
          }
          if (loc.duration) {
            responses.push({
              question_id: `${baseQid}__pain_duration`,
              answer: loc.duration,
              metric: "pain_duration",
              core_metric: "load",
              section_type: "status",
            });
          }
        }
      }
    });
    try {
      setSubmitting(true);
      await onSubmit?.(responses);
      toast.success(t('development:toast.reflection_saved', { defaultValue: t('development:eval_flow.player_summary_title') }));
    } catch (err) {
      console.error(err);
      toast.error(t('development:toast.reflection_save_failed', { defaultValue: 'Could not save. Please try again.' }));
    } finally {
      setSubmitting(false);
    }
  };

  if (!template) return null;

  return (
    <div
      data-testid="evaluation-form-v2"
      className="min-h-screen bg-gradient-to-b from-[rgb(var(--tn-bg-fafafa))] to-white dark:from-zinc-950 dark:to-[rgb(var(--tn-bg-000000))] flex flex-col"
    >
      {/* Header */}
      <header className="px-5 pt-5 pb-3 flex items-center gap-3">
        <button
          type="button"
          aria-label={t('common:cancel', { defaultValue: 'Avbryt' })}
          onClick={onCancel}
          data-testid="eval-v2-close-btn"
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-[rgb(var(--tn-bg-f4f4f5))] dark:hover:bg-[rgb(var(--tn-bg-18181b))] transition"
        >
          <X className="w-5 h-5 text-[rgb(var(--tn-text-71717a))]" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider text-[rgb(var(--tn-text-71717a))] font-medium">
            {isCoach ? t('development:eval_flow.kind_coach') : t('development:eval_flow.kind_player')} · {t('development:eval_flow.estimate_seconds', { seconds: estSeconds })}
          </p>
          <h1
            className="text-base font-semibold text-zinc-900 dark:text-[rgb(var(--tn-text-f4f4f5))] truncate"
            data-testid="eval-v2-title"
          >
            {template.name}
            {playerName ? ` · ${playerName}` : ""}
          </h1>
        </div>
      </header>

      {/* Progress */}
      <div className="px-5 pb-4">
        <Progress
          value={progressPct}
          className="h-1.5 bg-[rgb(var(--tn-bg-e4e4e7))] dark:bg-[rgb(var(--tn-bg-27272a))]"
          data-testid="eval-v2-progress"
        />
        <p className="mt-1.5 text-[11px] text-[rgb(var(--tn-text-71717a))]" data-testid="eval-v2-progress-text">
          {answeredCount} / {totalQuestions}
        </p>
      </div>

      {/* Card stage */}
      <main className="flex-1 px-5 pb-28 flex items-start justify-center">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait" custom={direction} initial={false}>
            <motion.div
              key={stepIndex}
              custom={direction}
              variants={cardVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ type: "spring", stiffness: 280, damping: 26 }}
              className="w-full"
            >
              {step?.kind === "question" && (
                <QuestionCard
                  question={step.question}
                  sectionMeta={SECTION_STYLE[step.section]}
                  isSectionStart={!!step.isSectionStart}
                  value={answers[step.question.id]}
                  onChange={(v) => setAnswer(step.question.id, v)}
                  onAutoAdvance={goNext}
                  painLocation={answers[`${step.question.id}__pain_loc`]}
                  onPainLocationChange={(loc) =>
                    setAnswer(`${step.question.id}__pain_loc`, loc)
                  }
                  isCoach={isCoach}
                  t={t}
                />
              )}

              {step?.kind === "summary" && (
                <SummaryCard
                  template={template}
                  answers={answers}
                  isCoach={isCoach}
                  submitting={submitting}
                  onSubmit={handleSubmit}
                  t={t}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Footer nav */}
      <footer className="fixed bottom-0 inset-x-0 bg-[rgb(var(--tn-tint-ffffff))]/90 dark:bg-black/80 backdrop-blur border-t border-[rgb(var(--tn-border-f4f4f5))] dark:border-[rgb(var(--tn-border-27272a))] px-5 py-3 flex items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={goBack}
          disabled={stepIndex === 0}
          data-testid="eval-v2-back-btn"
          className="rounded-full"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t('development:eval_flow.back')}
        </Button>
        <div className="flex-1" />
        {step?.kind !== "summary" && (
          <Button
            type="button"
            onClick={goNext}
            disabled={!canAdvance}
            data-testid="eval-v2-next-btn"
            className="rounded-full px-6 bg-[rgb(var(--tn-bg-18181b))] text-[rgb(var(--tn-text-ffffff))] hover:bg-[rgb(var(--tn-bg-27272a))] dark:bg-white dark:text-black"
          >
            {t('development:eval_flow.next')}
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </footer>
    </div>
  );
}

// ============================================================
// QuestionCard — one focused question
// Note: per Feb 2026 STATUS UX refinement we no longer repeat the
// "Detta är information om hur du mår" helper text on each question —
// the section intro carries that message exactly once.
// ============================================================
function QuestionCard({
  question,
  sectionMeta,
  isSectionStart,
  value,
  onChange,
  onAutoAdvance,
  painLocation,
  onPainLocationChange,
  isCoach,
  t,
}) {
  const Icon = sectionMeta?.icon;
  const isStatus = question.section_type === "status";
  const isReflection = question.section_type === "reflection";
  const showPainFollowup =
    !isCoach &&
    isPainQuestion(question) &&
    typeof value === "number" &&
    value >= PAIN_FOLLOWUP_THRESHOLD;

  // Auto-advance on rating selection — but never when a pain follow-up just
  // unlocked (the player must still pick area/side/duration before advancing).
  const handleChange = (v) => {
    onChange(v);
    if (
      onAutoAdvance &&
      (question.type === "rating_5" ||
        question.type === "rating_10" ||
        question.type === "emoji_5") &&
      !(isPainQuestion(question) && typeof v === "number" && v >= PAIN_FOLLOWUP_THRESHOLD)
    ) {
      // Small delay so the chosen value is visible briefly before sliding away.
      setTimeout(() => onAutoAdvance(), 280);
    }
  };

  return (
    <Card
      data-testid={`eval-v2-question-${question.id}`}
      className={`relative overflow-hidden rounded-3xl border-[rgb(var(--tn-border-e4e4e7))]/70 dark:border-[rgb(var(--tn-border-27272a))] shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-7 ${
        isStatus
          ? "bg-gradient-to-b from-[rgb(var(--tn-tint-f0f9ff))]/70 to-white dark:from-sky-950/30 dark:to-[rgb(var(--tn-bg-000000))]"
          : "bg-white dark:bg-zinc-950"
      }`}
    >
      <div className="flex items-center gap-2 mb-3">
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium uppercase tracking-wide ${sectionMeta.chip}`}
        >
          {Icon ? <Icon className="w-3 h-3" /> : null}
          {sectionMeta.label}
        </span>
      </div>

      {/* Section subtitle — shown only on the first question of each section,
          replaces the old "intro screen" without breaking the flow. */}
      {isSectionStart && sectionMeta.intro && (
        <p
          data-testid={`eval-v2-section-intro-${question.section_type || "development"}`}
          className="text-[12px] leading-relaxed text-[rgb(var(--tn-text-71717a))] dark:text-[rgb(var(--tn-text-a1a1aa))] mb-3"
        >
          {sectionMeta.intro}
        </p>
      )}

      <h2
        data-testid={`eval-v2-question-text-${question.id}`}
        className={`leading-snug ${
          isReflection
            ? "text-xl font-medium text-zinc-800 dark:text-[rgb(var(--tn-text-f4f4f5))]"
            : "text-2xl font-semibold text-zinc-900 dark:text-[rgb(var(--tn-text-fafafa))]"
        }`}
      >
        {question.text}
      </h2>

      {question.scale_description && <p className="mt-3 text-sm text-muted-foreground">{question.scale_description}</p>}
      <div className="mt-8">
        {question.type === "emoji_5" && (
          <EmojiScale value={value} onChange={handleChange} qid={question.id} />
        )}
        {question.type === "rating_5" && (
          <RatingScale
            max={5}
            value={value}
            onChange={handleChange}
            qid={question.id}
            inverted={!!question.scale_inverted}
            sectionType={question.section_type}
            isCoach={isCoach}
            scaleLabels={question.scale_labels}
            definitionSource={question.definition_source}
          />
        )}
        {question.type === "rating_10" && (
          <RatingScale
            max={10}
            value={value}
            onChange={handleChange}
            qid={question.id}
            inverted={!!question.scale_inverted}
            sectionType={question.section_type}
            isCoach={isCoach}
            scaleLabels={question.scale_labels}
            definitionSource={question.definition_source}
          />
        )}
        {question.allow_not_applicable && (
          <button type="button" aria-pressed={value === "not_applicable"}
            className={`mt-3 w-full rounded-xl border p-3 text-sm ${value === "not_applicable" ? "border-primary text-primary" : "border-border text-muted-foreground"}`}
            onClick={() => onChange("not_applicable")}>
            {t('development:question_not_applicable')}
          </button>
        )}
        {question.type === "text" && (
          <ReflectionField
            value={value || ""}
            onChange={onChange}
            qid={question.id}
            placeholder={
              isCoach
                ? t('development:eval_flow.coach_reflection_placeholder')
                : t('development:eval_flow.player_reflection_placeholder')
            }
            optionalLabel={t('development:eval_flow.optional')}
          />
        )}
      </div>

      {showPainFollowup && (
        <div className="mt-6">
          <PainLocationPicker
            value={painLocation}
            onChange={onPainLocationChange}
          />
        </div>
      )}

      {isPainQuestion(question) && question.warning_threshold && value >= question.warning_threshold && (
        <p
          data-testid={`eval-v2-warning-${question.id}`}
          className="mt-5 text-xs text-amber-700 bg-[rgb(var(--tn-bg-fffbeb))] border border-amber-200 rounded-xl px-3 py-2 dark:bg-amber-950/40 dark:text-[rgb(var(--tn-text-fde68a))]"
        >
          {t('development:eval_flow.pain_followup_message')}
        </p>
      )}
    </Card>
  );
}

// ============================================================
// ReflectionField — optional academy-tone textarea
// ============================================================
function ReflectionField({ value, onChange, qid, placeholder, optionalLabel }) {
  return (
    <div>
      <Textarea
        data-testid={`eval-v2-text-${qid}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={4}
        className="rounded-2xl text-base resize-none border-[rgb(var(--tn-border-e4e4e7))] focus-visible:ring-[rgb(var(--tn-border-18181b))]/10"
      />
      <p className="mt-2 text-[11px] text-[rgb(var(--tn-text-a1a1aa))]">{optionalLabel}</p>
    </div>
  );
}

// ============================================================
// SummaryCard — final card before submit
// ============================================================
function SummaryCard({ template, answers, isCoach, submitting, onSubmit, t }) {
  const ratingQs = (template.questions || []).filter((q) =>
    q.type !== "text"
  );
  const answeredRatings = ratingQs.filter(
    (q) => answers[q.id] !== undefined && answers[q.id] !== ""
  ).length;

  return (
    <Card
      data-testid="eval-v2-summary"
      className="rounded-3xl bg-white dark:bg-zinc-950 border-[rgb(var(--tn-border-e4e4e7))]/70 dark:border-[rgb(var(--tn-border-27272a))] shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-8 text-center"
    >
      <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mb-5">
        <Check className="w-7 h-7 text-[rgb(var(--tn-text-059669))]" />
      </div>
      <h2 className="text-2xl font-semibold text-zinc-900 dark:text-[rgb(var(--tn-text-fafafa))]">
        {isCoach ? t('development:eval_flow.coach_summary_title') : t('development:eval_flow.player_summary_title')}
      </h2>
      <p className="mt-3 text-sm text-[rgb(var(--tn-text-71717a))] max-w-xs mx-auto leading-relaxed">
        {isCoach
          ? t('development:eval_flow.coach_summary_subtitle')
          : t('development:eval_flow.player_summary_subtitle')}
      </p>

      <div className="mt-6 inline-flex items-center gap-2 text-xs text-[rgb(var(--tn-text-71717a))]">
        {t('development:eval_flow.summary_progress', { answered: answeredRatings, total: ratingQs.length })}
      </div>

      <Button
        type="button"
        onClick={onSubmit}
        disabled={submitting}
        data-testid="eval-v2-submit-btn"
        className="mt-8 w-full h-12 rounded-full text-base bg-[rgb(var(--tn-bg-18181b))] text-[rgb(var(--tn-text-ffffff))] hover:bg-[rgb(var(--tn-bg-27272a))] dark:bg-white dark:text-black"
      >
        {submitting ? t('development:eval_flow.saving') : isCoach ? t('development:eval_flow.coach_submit') : t('development:eval_flow.player_submit')}
      </Button>
    </Card>
  );
}
